import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowUpRight, BookOpen, Award, TrendingUp } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/me/enrollments")
      .then(r => setEnrollments(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen pt-32 relative overflow-hidden">
      <div className="orb orb-violet w-[28rem] h-[28rem] -top-40 right-[10%] opacity-60" aria-hidden="true" />
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-12 relative">
        <div className="overline-label mb-4">Dashboard</div>
        <h1 className="font-display font-bold tracking-tighter leading-[1.05] text-4xl sm:text-5xl text-white">
          Hello, <span className="text-gradient-glow">{user?.name?.split(" ")[0]}.</span>
        </h1>
        <p className="text-slate-200 mt-4 font-bn">Your AI learning journey lives here. What's today's goal?</p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="card-void p-7" data-testid="stat-enrolled">
            <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-400/30 flex items-center justify-center mb-4">
              <BookOpen className="text-orange-400" size={18} aria-hidden="true" />
            </div>
            <div className="font-display text-4xl font-bold text-white">{enrollments.length}</div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-slate-200 mt-2 font-semibold">Enrolled Courses</div>
          </div>
          <div className="card-void p-7" data-testid="stat-progress">
            <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-400/30 flex items-center justify-center mb-4">
              <TrendingUp className="text-orange-400" size={18} aria-hidden="true" />
            </div>
            <div className="font-display text-4xl font-bold text-white">
              {enrollments.length ? Math.round(enrollments.reduce((s, e) => s + (e.enrollment.progress || 0), 0) / enrollments.length) : 0}%
            </div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-slate-200 mt-2 font-semibold">Avg Progress</div>
          </div>
          <div className="card-void p-7" data-testid="stat-certificates">
            <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-400/30 flex items-center justify-center mb-4">
              <Award className="text-orange-400" size={18} aria-hidden="true" />
            </div>
            <div className="font-display text-4xl font-bold text-white">0</div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-slate-200 mt-2 font-semibold">Certificates</div>
          </div>
        </div>

        <div className="mt-16">
          <h2 className="font-display text-2xl font-bold text-white mb-6">My Courses</h2>

          {loading ? (
            <div className="text-slate-200">Loading…</div>
          ) : enrollments.length === 0 ? (
            <div className="border-2 border-dashed border-white/10 rounded-3xl p-12 text-center bg-white/[0.03]">
              <p className="text-slate-200 mb-6 font-bn">You haven't started any lessons yet.</p>
              <Link to="/lessons" className="btn-glow inline-flex" data-testid="empty-explore-btn">
                Explore Lessons <ArrowUpRight size={16} />
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              {enrollments.map((e) => (
                <div
                  key={e.enrollment.id}
                  className="card-void p-6 group flex gap-5"
                  data-testid={`enrolled-${e.course.slug}`}
                >
                  <div className="w-24 h-24 overflow-hidden rounded-2xl flex-shrink-0">
                    <img src={e.course.cover_image} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <span className="chip mb-2">{e.course.category}</span>
                    <h3 className="font-display font-semibold text-lg leading-snug text-white">{e.course.title}</h3>
                    <div className="mt-4">
                      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${e.enrollment.progress || 0}%`, background: "linear-gradient(90deg, #F97316, #EA580C)", boxShadow: "0 0 10px rgba(249,115,22,0.5)" }} />
                      </div>
                      <div className="text-xs text-slate-200 mt-2">{e.enrollment.progress || 0}% complete</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
